import { createContext, type CSSProperties, type ReactNode, useLayoutEffect, useState, useRef } from 'react';

import Theme from '../../enums/Theme.enum';


export type ChapterPlayerContextType = {
  width: number,
  height: number,
  languages: string[],
  channel: object,
  theme?: Theme,
};

const ChapterPlayerContext = createContext<ChapterPlayerContextType>({
  width: 0,
  height: 0,
  languages: [],
  channel: {},
});

export function ChapterPlayerProvider({ children, value, style }: {
  children: ReactNode,
  value: Omit<ChapterPlayerContextType, 'width' | 'height'>,
  style?: CSSProperties,
}) {
  const container = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 1080, height: 1920 });

  useLayoutEffect(() => {
    const node = container.current;
    if (!node) {
      return;
    }

    const updateSize = () => {
      const { width, height } = node.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setSize({ width, height });
      }
    };

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={container}
      style={{
        position: 'absolute',
        top: 0,
        ...style,
      }}
    >
      <ChapterPlayerContext.Provider
        value={{
          ...value,
          width: size.width,
          height: size.height,
        }}
      >
        {children}
      </ChapterPlayerContext.Provider>
    </div>
  );
}

export default ChapterPlayerContext;
